/* =====================================================
   THE ARCHIVE ENGINE v2.0
   The Lost Wax Company
===================================================== */

window.ArchiveEngine = (function () {
  const STORAGE_KEY = 'lostwax_archive_progress';
  const OLD_STORAGE_KEY = 'lostwax_archive_solved';

  const STAMP_URL =
    'https://cdn.shopify.com/s/files/1/0776/8542/4368/files/Dept-of-Obfuscation-Red-Stamp.png?v=1782182194';

  const DRAWER_STAMP_URL =
    'https://cdn.shopify.com/s/files/1/0776/8542/4368/files/Catalogued.png?v=1783271655';

  const DRAWER_STATES = {
    RESTRICTED: 'restricted',
    ACTIVE: 'active',
    CATALOGUED: 'catalogued'
  };

  /* -----------------------------
     Storage / Notebook
  ----------------------------- */

  function loadProgress() {
    let progress = null;

    try {
      progress = JSON.parse(localStorage.getItem(STORAGE_KEY));
    } catch (error) {
      progress = null;
    }

    if (progress && Array.isArray(progress.solved)) {
      return normalizeProgress(progress);
    }

    try {
      const oldSolved = JSON.parse(localStorage.getItem(OLD_STORAGE_KEY)) || [];

      if (Array.isArray(oldSolved) && oldSolved.length) {
        progress = {
          version: 2,
          solved: oldSolved,
          lastUpdated: new Date().toISOString()
        };

        progress = normalizeProgress(progress);

        localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
        localStorage.removeItem(OLD_STORAGE_KEY);

        return progress;
      }
    } catch (error) {}

    return {
      version: 2,
      solved: []
    };
  }

  function saveProgress(progress) {
    const normalized = normalizeProgress(progress);
    normalized.lastUpdated = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    return normalized;
  }

  function normalizeProgress(progress) {
    const solved = Array.isArray(progress.solved) ? progress.solved : [];

    return {
      ...progress,
      version: 2,
      solved: unique(
        solved
          .map(function (id) {
            return normalizeRecordId(id);
          })
          .filter(Boolean)
      )
    };
  }

  function catalog(recordId) {
    const progress = loadProgress();
    const normalizedId = normalizeRecordId(recordId);

    if (normalizedId && !progress.solved.includes(normalizedId)) {
      progress.solved.push(normalizedId);
    }

    saveProgress(progress);
    return progress.solved;
  }

  function isCatalogued(recordId) {
    const progress = loadProgress();
    return progress.solved.includes(normalizeRecordId(recordId));
  }

  /* -----------------------------
     Cabinet Page
  ----------------------------- */

  function initCabinet() {
    const progress = loadProgress();
    const solvedRecords = progress.solved;
    const drawers = getDrawers();
    const drawerStates = determineDrawerStates(drawers, solvedRecords);

    updatePuzzleCards(solvedRecords);
    updateDrawers(drawers, drawerStates, solvedRecords);
    updateDashboard(solvedRecords, drawerStates);
  }

  function getDrawers() {
    return Array.from(document.querySelectorAll('[data-archive-drawer]')).sort(
      function (a, b) {
        return getDrawerNumber(a) - getDrawerNumber(b);
      }
    );
  }

  function determineDrawerStates(drawers, solvedRecords) {
    const states = {};
    let activeDrawerFound = false;

    drawers.forEach(function (drawer) {
      const drawerNumber = getDrawerNumber(drawer);
      const isComplete = isDrawerComplete(drawer, solvedRecords);

      if (isComplete && !activeDrawerFound) {
        states[drawerNumber] = DRAWER_STATES.CATALOGUED;
        return;
      }

      if (!activeDrawerFound) {
        states[drawerNumber] = DRAWER_STATES.ACTIVE;
        activeDrawerFound = true;
        return;
      }

      states[drawerNumber] = DRAWER_STATES.RESTRICTED;
    });

    return states;
  }

  function updateDrawers(drawers, drawerStates, solvedRecords) {
    drawers.forEach(function (drawer) {
      const drawerNumber = getDrawerNumber(drawer);
      const state =
        drawerStates[drawerNumber] || DRAWER_STATES.RESTRICTED;

      setDrawerStateClass(drawer, state);
      updateDrawerProgress(drawer, state, solvedRecords);
      updateDrawerContentVisibility(drawer, state);

      if (state === DRAWER_STATES.CATALOGUED) {
        addDrawerStamp(drawer);
      } else {
        removeDrawerStamp(drawer);
      }
    });
  }

  function setDrawerStateClass(drawer, state) {
    drawer.dataset.drawerState = state;
    drawer.dataset.drawerUnlocked =
      state === DRAWER_STATES.RESTRICTED ? 'false' : 'true';

    drawer.classList.remove(
      'archive-drawer--restricted',
      'archive-drawer--active',
      'archive-drawer--catalogued'
    );

    drawer.classList.add('archive-drawer--' + state);
  }

  function updateDrawerProgress(drawer, state, solvedRecords) {
    const titleEl = drawer.querySelector('[data-drawer-title]');
    const descriptionEl = drawer.querySelector(
      '[data-drawer-description]'
    );
    const messageEl = drawer.querySelector('[data-drawer-message]');
    const progressEl = drawer.querySelector('[data-drawer-progress]');
    const panel = drawer.querySelector('.archive-drawer__panel');

    const normalDescription = drawer.querySelector(
      '[data-drawer-normal-description]'
    );

    const normalMessage = drawer.querySelector(
      '[data-drawer-normal-message]'
    );

    if (!progressEl || !panel) return;

    if (state === DRAWER_STATES.RESTRICTED) {
      if (titleEl) {
        titleEl.textContent =
          panel.dataset.drawerRestrictedTitle || 'Restricted Access';
      }

      if (descriptionEl) {
        descriptionEl.textContent =
          panel.dataset.drawerRestrictedDescription ||
          'The description of these files is withheld pending further review.';
      }

      if (messageEl && normalMessage) {
        messageEl.innerHTML = normalMessage.innerHTML.trim();
      }

      progressEl.textContent = 'Access Restricted';
      return;
    }

    if (titleEl) {
      titleEl.textContent = panel.dataset.drawerNormalTitle || '';
    }

    if (descriptionEl && normalDescription) {
      descriptionEl.innerHTML = normalDescription.innerHTML.trim();
    }

    if (messageEl && normalMessage) {
      messageEl.innerHTML = normalMessage.innerHTML.trim();
    }

    if (state === DRAWER_STATES.CATALOGUED) {
      progressEl.textContent = 'Complete';
      return;
    }

    const solvedInDrawer = getSolvedCountInDrawer(
      drawer,
      solvedRecords
    );

    const totalInDrawer = getRecordCards(drawer).length;

    progressEl.textContent =
      solvedInDrawer + ' / ' + totalInDrawer + ' Catalogued';
  }

  function updateDrawerContentVisibility(drawer, state) {
    const cards = drawer.querySelector(
      '[data-drawer-open-content]'
    );

    const restricted = drawer.querySelector(
      '[data-drawer-restricted-content]'
    );

    if (!cards || !restricted) return;

    if (state === DRAWER_STATES.RESTRICTED) {
      cards.hidden = true;
      restricted.hidden = false;
    } else {
      cards.hidden = false;
      restricted.hidden = true;
    }
  }

  function updatePuzzleCards(solvedRecords) {
    document
      .querySelectorAll('.archive-card[data-record-id]')
      .forEach(function (card) {
        const recordId = normalizeRecordId(card.dataset.recordId);
        const solved =
          recordId && solvedRecords.includes(recordId);

        card.classList.toggle('archive-card--solved', solved);

        if (solved) {
          updatePlaque(card, true);
          addStamp(card);
        } else {
          updatePlaque(card, false);
        }
      });
  }

  function updatePlaque(card, solved) {
    const plaque = card.querySelector('[data-plaque]');
    if (!plaque) return;

    plaque.classList.remove(
      'archive-plaque-button--begin',
      'archive-plaque-button--review'
    );

    if (solved) {
      plaque.classList.add('archive-plaque-button--review');
      plaque.setAttribute('aria-label', 'Review File');

      const href = plaque.getAttribute('href');

      if (href && !href.includes('review=1')) {
        plaque.setAttribute(
          'href',
          href + (href.includes('?') ? '&' : '?') + 'review=1'
        );
      }
    } else {
      plaque.classList.add('archive-plaque-button--begin');
      plaque.setAttribute(
        'aria-label',
        'Begin Investigation'
      );
    }
  }

  function updateDashboard(solvedRecords, drawerStates) {
    const totalSolvedEl = document.querySelector(
      '[data-archive-total-solved]'
    );

    const currentDrawerEl = document.querySelector(
      '[data-archive-current-drawer]'
    );

    if (totalSolvedEl) {
      totalSolvedEl.textContent = solvedRecords.length;
    }

    if (currentDrawerEl) {
      const activeDrawer =
        getActiveDrawerNumber(drawerStates);

      currentDrawerEl.textContent =
        'Drawer ' + toRoman(activeDrawer);
    }
  }

  function getActiveDrawerNumber(drawerStates) {
    const active = Object.keys(drawerStates).find(
      function (drawerNumber) {
        return (
          drawerStates[drawerNumber] ===
          DRAWER_STATES.ACTIVE
        );
      }
    );

    if (active) return Number(active);

    const catalogued = Object.keys(drawerStates)
      .map(Number)
      .sort(function (a, b) {
        return b - a;
      })[0];

    return catalogued || 1;
  }

  function isDrawerComplete(drawer, solvedRecords) {
    const cards = getRecordCards(drawer);

    if (!cards.length) return false;

    return cards.every(function (card) {
      const recordId = normalizeRecordId(
        card.dataset.recordId
      );

      return (
        recordId &&
        solvedRecords.includes(recordId)
      );
    });
  }

  function getSolvedCountInDrawer(drawer, solvedRecords) {
    return getRecordCards(drawer).filter(
      function (card) {
        const recordId = normalizeRecordId(
          card.dataset.recordId
        );

        return (
          recordId &&
          solvedRecords.includes(recordId)
        );
      }
    ).length;
  }

  function getRecordCards(drawer) {
    return Array.from(
      drawer.querySelectorAll(
        '.archive-card[data-record-id]'
      )
    );
  }

  function getDrawerNumber(drawer) {
    return Number(drawer.dataset.drawerNumber) || 1;
  }

  /* -----------------------------
     Stamps
  ----------------------------- */

  function addStamp(card) {
    if (card.querySelector('.archive-stamp-image')) return;

    const stamp = document.createElement('img');

    stamp.className = 'archive-stamp-image';
    stamp.src =
      card.dataset.validationMark || STAMP_URL;
    stamp.alt =
      'Verified by the Department of Obfuscation';
    stamp.loading = 'lazy';

    const variation = getStableStampVariation(
      card.dataset.recordId
    );

    stamp.style.transform =
      'rotate(' + variation.rotation + 'deg)';

    stamp.style.right =
      variation.right + 'rem';

    stamp.style.bottom =
      variation.bottom + 'rem';

    card.appendChild(stamp);
  }

  function addDrawerStamp(drawer) {
    const panel = drawer.querySelector(
      '.archive-drawer__panel'
    );

    if (!panel) return;

    if (
      panel.querySelector(
        '.archive-drawer-stamp-image'
      )
    ) {
      return;
    }

    const stamp = document.createElement('img');

    stamp.className =
      'archive-drawer-stamp-image';

    stamp.src = DRAWER_STAMP_URL;
    stamp.alt = 'Drawer Catalogued';
    stamp.loading = 'lazy';

    const variation =
      getStableDrawerStampVariation(
        'DRAWER-' + getDrawerNumber(drawer)
      );

    stamp.style.transform =
      'translateX(-50%) rotate(' +
      variation.rotation +
      'deg)';

    stamp.style.left =
      variation.left + '%';

    stamp.style.top =
      variation.top + 'rem';

    panel.appendChild(stamp);
  }

  function removeDrawerStamp(drawer) {
    const stamp = drawer.querySelector(
      '.archive-drawer-stamp-image'
    );

    if (stamp) stamp.remove();
  }

  function getStableStampVariation(recordId) {
    const hash = hashString(recordId || '');

    return {
      rotation: (hash % 25) - 12,
      right: 0.35 + (hash % 8) * 0.18,
      bottom: 10.05 + (hash % 10) * 0.12
    };
  }

  function getStableDrawerStampVariation(id) {
    const hash = hashString(id);

    return {
      rotation: (hash % 13) - 6,
      left: 48 + (hash % 7),
      top: 40 + (hash % 16) * 0.35
    };
  }

  function hashString(value) {
    let hash = 0;
    const string = String(value || '');

    for (let i = 0; i < string.length; i++) {
      hash =
        (hash << 5) -
        hash +
        string.charCodeAt(i);

      hash |= 0;
    }

    return Math.abs(hash);
  }

  /* -----------------------------
     Utilities
  ----------------------------- */

  function normalizeRecordId(value) {
    return String(value || '')
      .trim()
      .toUpperCase();
  }

  function normalizePhrase(value) {
    return String(value || '')
      .trim()
      .toUpperCase()
      .replace(/-/g, ' ')
      .replace(/[.,!?;:'"“”‘’]/g, '')
      .replace(/\s+/g, ' ');
  }

  function unique(array) {
    return Array.from(new Set(array));
  }

  function toRoman(num) {
    const values = [
      [10, 'X'],
      [9, 'IX'],
      [5, 'V'],
      [4, 'IV'],
      [1, 'I']
    ];

    let result = '';
    let n = Number(num) || 1;

    values.forEach(function (pair) {
      while (n >= pair[0]) {
        result += pair[1];
        n -= pair[0];
      }
    });

    return result || 'I';
  }

  return {
    initCabinet: initCabinet,
    loadProgress: loadProgress,
    saveProgress: saveProgress,
    catalog: catalog,
    isCatalogued: isCatalogued
  };
})();